import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Receipt, BadgeCheck, BellRing, Save } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const MoneyField = ({ label, hint, icon: Icon, value, onChange, testid }) => (
  <div className="border border-[var(--line)] bg-white p-5">
    <p className="font-medium flex items-center gap-2"><Icon className="w-4 h-4 text-[var(--accent)]" /> {label}</p>
    <p className="text-xs text-[var(--text-2)] mt-1 mb-3">{hint}</p>
    <div className="flex items-center border border-[var(--line)] focus-within:border-[var(--accent)] h-11 px-3 w-44">
      <span className="text-[var(--text-2)] mr-1">$</span>
      <input data-testid={testid} type="number" min="0" step="1" value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-transparent font-mono outline-none" />
    </div>
  </div>
);

export default function PoliciesPanel() {
  const [pol, setPol] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/business/policies").then((r) => setPol(r.data)).catch(() => {}); }, []);
  if (!pol) return <p className="text-sm text-[var(--text-2)]" data-testid="policies-loading">Loading policies…</p>;
  const set = (k, v) => setPol((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/business/policies", { receipt_threshold: Number(pol.receipt_threshold) || 0, approval_threshold: Number(pol.approval_threshold) || 0, receipt_reminder: pol.receipt_reminder });
      setPol(data); toast.success("Policies saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <div data-testid="policies-panel" className="space-y-5">
      <div>
        <h2 className="text-xl font-bold flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[var(--accent)]" /> Spend Policies</h2>
        <p className="text-sm text-[var(--text-2)] mt-1">Rules applied to business expenses in SnapBudget. Flags show up there automatically.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MoneyField testid="policy-receipt-threshold" icon={Receipt} label="Receipt required over" hint="Business expenses at or above this amount get a “Receipt missing” flag until a receipt is attached. IRS guidance: $75." value={pol.receipt_threshold} onChange={(v) => set("receipt_threshold", v)} />
        <MoneyField testid="policy-approval-threshold" icon={BadgeCheck} label="Approval required over" hint="Business expenses at or above this amount show “Needs approval” until you approve them. Set 0 to turn off." value={pol.approval_threshold} onChange={(v) => set("approval_threshold", v)} />
      </div>
      <label data-testid="policy-receipt-reminder" className="flex items-center justify-between gap-4 border border-[var(--line)] bg-white p-5 cursor-pointer">
        <span>
          <span className="font-medium flex items-center gap-2"><BellRing className="w-4 h-4 text-[var(--accent)]" /> Receipt reminder</span>
          <span className="block text-xs text-[var(--text-2)] mt-1">Show the missing-receipt checklist in SnapBudget so nothing slips before tax time.</span>
        </span>
        <input type="checkbox" checked={!!pol.receipt_reminder} onChange={(e) => set("receipt_reminder", e.target.checked)} className="w-5 h-5 accent-[var(--accent)]" />
      </label>
      <button data-testid="policies-save" onClick={save} disabled={saving} className="h-11 px-6 bg-[#1A1A18] text-white hover:bg-[var(--accent)] transition-colors disabled:opacity-50 flex items-center gap-2"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save policies"}</button>
    </div>
  );
}
